import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { seoFor, canonicalFor, DEFAULT_OG_IMAGE, SITE_URL } from '../utils/seoConfig';

// SEO — mutates <head> for the current route. Uses direct DOM writes (no
// helmet dep) so the bundle stays lean and no SSR wiring is needed. Every
// tag is data-tagged with `data-seo="dynamic"` so the effect can safely
// remove its own leftovers when the page unmounts.
//
// Usage:
//   <SEO />                                        // pulls from seoConfig
//   <SEO title="…" description="…" />              // per-page override
//   <SEO jsonLd={{ '@context': '...', ... }} />    // attach schema.org
//
// Precedence: props > seoConfig for that pathname > global default.

const DATA_ATTR = 'data-seo';

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(DATA_ATTR, 'dynamic');
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(DATA_ATTR, 'dynamic');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, obj) {
  if (!obj) return;
  let el = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute(DATA_ATTR, 'dynamic');
    el.setAttribute('data-seo-id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

function removeJsonLd(id) {
  const el = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  robots = 'index,follow',
  jsonLd,   // object or array of objects — per-page schema.org
}) {
  const { pathname } = useLocation();

  useEffect(() => {
    const preset = seoFor(pathname);
    const finalTitle       = title       || preset.title;
    const finalDescription = description || preset.description;
    const finalCanonical   = canonical   || canonicalFor(pathname);
    const finalOgImage     = ogImage     || DEFAULT_OG_IMAGE;

    if (finalTitle) document.title = finalTitle;

    upsertMeta('name',     'description', finalDescription);
    upsertMeta('name',     'robots',      robots);
    upsertLink('canonical', finalCanonical);

    // Open Graph
    upsertMeta('property', 'og:title',       finalTitle);
    upsertMeta('property', 'og:description', finalDescription);
    upsertMeta('property', 'og:url',         finalCanonical);
    upsertMeta('property', 'og:image',       finalOgImage);
    upsertMeta('property', 'og:type',        'website');
    upsertMeta('property', 'og:locale',      'en_MY');
    upsertMeta('property', 'og:site_name',   'MyDr.Ai');

    // Twitter
    upsertMeta('name', 'twitter:card',        'summary_large_image');
    upsertMeta('name', 'twitter:title',       finalTitle);
    upsertMeta('name', 'twitter:description', finalDescription);
    upsertMeta('name', 'twitter:image',       finalOgImage);

    // Page-scoped JSON-LD (organisation + website are already in index.html)
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item, i) => upsertJsonLd(`page-${i}`, item));
      // Clean up any prior blocks from a previous page that had more entries.
      for (let i = items.length; i < 10; i++) removeJsonLd(`page-${i}`);
    } else {
      for (let i = 0; i < 10; i++) removeJsonLd(`page-${i}`);
    }
  }, [pathname, title, description, canonical, ogImage, robots, jsonLd]);

  return null;
}

// Convenience: build a WebPage/BreadcrumbList schema for a route.
export function webPageSchema(pathname, name, description) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}${pathname}#webpage`,
    url: `${SITE_URL}${pathname}`,
    name,
    description,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    inLanguage: 'en-MY',
  };
}
